// Currency Formatting Utility
// Formats amounts in European style: 1.200,00

/**
 * Format a number as currency with European formatting
 * @param {number} amount - The amount to format
 * @param {string} currency - Currency code (USD, EUR, ANG, AWG, XCG, etc.)
 * @param {string} locale - Locale for formatting (default: nl-NL for European style)
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount, currency = 'USD', locale = 'nl-NL') => {
  if (amount === null || amount === undefined || isNaN(amount)) {
    return `${getCurrencySymbol(currency)} 0,00`;
  }
  
  const numAmount = parseFloat(amount);
  
  // Format with European style (1.234,56)
  const formatted = numAmount.toLocaleString(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  
  const symbol = getCurrencySymbol(currency);
  
  // For currencies like XCG that typically go after the number
  if (['XCG', 'ANG', 'AWG'].includes(currency.toUpperCase())) {
    return `${formatted} ${currency}`;
  }
  
  return `${symbol} ${formatted}`;
};

/**
 * Format a price for display (handles various price types)
 * @param {object} pricing - Pricing object with amount, currency, price_type, rental_period
 * @param {string} language - Language code for translations (en, nl, es)
 * @returns {string} Formatted price string
 */
export const formatPrice = (pricing, language = 'en') => {
  if (!pricing || !pricing.amount) {
    return 'Price on request';
  }
  
  const { amount, currency = 'USD', price_type, is_hourly, rental_period } = pricing;
  
  let formatted = formatCurrency(amount, currency);
  
  // Add price type suffix
  if (price_type === 'incl_ob') {
    formatted += ' incl. OB';
  } else if (price_type === 'excl_ob') {
    formatted += ' excl. OB';
  }
  
  // Add rental period indicator (new system)
  if (rental_period && rental_period !== 'one_time') {
    formatted += ` / ${getRentalPeriodLabel(rental_period, language)}`;
  }
  // Backwards compatibility: support old is_hourly field
  else if (is_hourly) {
    formatted += ` / ${getRentalPeriodLabel('hourly', language)}`;
  }
  
  return formatted;
};

/**
 * Get translated rental period label
 * @param {string} period - Rental period: hourly, daily, weekly, monthly, yearly
 * @param {string} language - Language code: en, nl, es
 * @returns {string} Translated label
 */
export const getRentalPeriodLabel = (period, language = 'en') => {
  const labels = {
    hourly: { en: 'hour', nl: 'uur', es: 'hora' },
    daily: { en: 'day', nl: 'dag', es: 'día' },
    weekly: { en: 'week', nl: 'week', es: 'semana' },
    monthly: { en: 'month', nl: 'maand', es: 'mes' },
    yearly: { en: 'year', nl: 'jaar', es: 'año' },
  };
  
  return labels[period]?.[language] || labels[period]?.en || period;
};

/**
 * Get full translated rental period text (e.g., "Per month")
 * @param {string} period - Rental period
 * @param {string} language - Language code
 * @returns {string} Full translated text
 */
export const getRentalPeriodFullLabel = (period, language = 'en') => {
  const labels = {
    hourly: { en: 'Per hour', nl: 'Per uur', es: 'Por hora' },
    daily: { en: 'Per day', nl: 'Per dag', es: 'Por día' },
    weekly: { en: 'Per week', nl: 'Per week', es: 'Por semana' },
    monthly: { en: 'Per month', nl: 'Per maand', es: 'Por mes' },
    yearly: { en: 'Per year', nl: 'Per jaar', es: 'Por año' },
  };
  
  return labels[period]?.[language] || labels[period]?.en || '';
};

/**
 * Get currency symbol for a currency code
 * @param {string} currency - Currency code
 * @returns {string} Currency symbol
 */
export const getCurrencySymbol = (currency) => {
  const symbols = {
    USD: '$',
    EUR: '€',
    ANG: 'XCG ', // Netherlands Antillean guilder
    AWG: 'XCG ', // Aruban florin
    XCG: '', // Caribbean guilder (no symbol, use code)
    GBP: '£',
    BTC: '₿',
  };
  
  return symbols[currency?.toUpperCase()] || currency || '$';
};

/**
 * Format a number with thousand separators (European style)
 * @param {number} num - Number to format
 * @returns {string} Formatted number
 */
export const formatNumber = (num) => {
  if (num === null || num === undefined || isNaN(num)) {
    return '0';
  }
  return parseFloat(num).toLocaleString('nl-NL');
};

/**
 * Parse a formatted currency string back to number
 * @param {string} str - Formatted currency string
 * @returns {number} Parsed number
 */
export const parseCurrency = (str) => {
  if (!str) return 0;
  // Remove currency symbols and spaces, convert comma to dot
  const cleaned = str.replace(/[^0-9,.-]/g, '').replace(/\./g, '').replace(',', '.');
  return parseFloat(cleaned) || 0;
};

export default {
  formatCurrency,
  formatPrice,
  getCurrencySymbol,
  formatNumber,
  parseCurrency,
  getRentalPeriodLabel,
  getRentalPeriodFullLabel,
};
